import { Component, OnInit, Input, ViewChild, OnDestroy, AfterViewInit, ElementRef, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { User } from '../../models/user';
import { ClientDetails } from '../../../clients/models/client-details';
import { ClientParent } from '../../../client-parents/models/client-parent';
import { UserLimiter } from '../../models/user-limiter';
import { UserEditorComponent } from '../user-editor/user-editor.component';
import * as userActions from '../../actions/users';
import { UserService } from '../../services/user.service';
import { ToastrService } from 'ngx-toastr'
import { distinctUntilChanged, of } from 'rxjs';
import { catchError, takeWhile, tap } from 'rxjs';
import { BrandService } from '../../../brand/services/brand.service';
import { UserInviteRequest } from '../../models/user-invite-request';
import { UserInviteResult } from '../../models/user-invite-result';
import { UserDisableRequest } from '../../models/user-disable-request';
import { PrimeColumn } from '../../../common/models/prime-column';
import { ColumnDefinitionFormatType as FormatType } from '../../../common/enums/column-definition-format-type';
import { ExportCsvService } from "@shared/export-csv/services/export-csv-data.service";
import { QueryBuilderConfig, RuleSet } from 'ngx-angular-query-builder';
import {
  FilterEditorDialogComponent,
  FilterEditorDialogResult,
  getListTypeFilter,
  QueryBuilderConfigProvider
} from "@feature/common";
import * as filterActions from "@common/filter/actions/filter.actions";
import { ListType } from '@common/enums/list-type';
import {StandardFilterStrategy} from "@common/filter/standard-filter-strategy";
import { MciUserFilterFieldProvider } from '@feature/users/filter/mci-user-filter-field-provider';
import * as filterUtils from "@common/filter/filter-utils";

@Component({
  selector: 'mojo-user-table',
  templateUrl: './user-table.component.html'
})
export class UserTableComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  readonly listType: ListType.MciManageMyUsers = ListType.MciManageMyUsers;

  @ViewChild(UserEditorComponent, { static: true }) userEditor;
  @ViewChild(FilterEditorDialogComponent, { static: true }) filterDialog;

  @Input()
  users: User[] = [];
  @Input()
  clients: ClientDetails[];
  @Input()
  clientParents: ClientParent[];
  @Input()
  loading = false;
  @Input()
  limitedByOptions: UserLimiter[];
  @Input()
  displayExportButton = true;
  @Input()
  user: User = new User();

  selectedUsers: User[] = [];
  isAlive: boolean = true;
  brand: string;

  @ViewChild('container', { static: true }) dataTableContainerElement: ElementRef;
  @Output()
  onSelectionChanged: EventEmitter<any> = new EventEmitter();

  selectAllState: 'none' | 'indeterminate' | 'all' = 'none';
  selectAll = false;

  queryConfig: QueryBuilderConfig;
  currentFilter: RuleSet;
  filteredUsers: User[] = [];

  constructor(private readonly store: Store<any>,
    private readonly messageService: ToastrService,
    private readonly userService: UserService,
    private readonly brandService: BrandService,
    private readonly exportCsvService: ExportCsvService) {
  }

  getPrimeColumns(): PrimeColumn[] {
    const columns: PrimeColumn[] = [];
    columns.push(
      {
        field: 'email',
        header: 'Email Address',
        width: 80,
        type: FormatType.String
      } as PrimeColumn,
      {
        field: 'role',
        header: 'Role',
        width: 80,
        type: FormatType.String
      } as PrimeColumn,
      {
        field: 'limitedByString',
        header: 'Limited to',
        width: 80,
        type: FormatType.String,
      } as PrimeColumn,
      {
        field: 'status',
        header: 'Status',
        width: 80,
        type: FormatType.String,
      } as PrimeColumn,
      {
        field: 'createdDate',
        header: 'Created Date',
        width: 80,
        type: FormatType.Date,
      } as PrimeColumn,
      {
        field: 'invitedDate',
        header: 'Invited Date',
        width: 80,
        type: FormatType.Date,
      } as PrimeColumn,
      {
        field: 'emailConfirmedDate',
        header: 'Email Confirmed Date',
        width: 80,
        type: FormatType.Date,
      } as PrimeColumn,
    );
    return columns;

  }

  ngOnInit(): void {
    this.brandService.getCustomerBrandName().pipe(takeWhile(() => this.isAlive)).subscribe((brandName: string) => {
      this.brand = brandName;
    });

    this.store.dispatch(new filterActions.LoadListTypeFilter(new filterActions.ListTypeFilterActionPayload(this.listType)));
    this.store
      .pipe(select(getListTypeFilter(this.listType)))
      .pipe(distinctUntilChanged())
      .subscribe((f: RuleSet) => {
        this.currentFilter = f;
        this.applyUsersOrFilterUpdate();
      });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users?.previousValue !== changes.users?.currentValue) {
      this.applyUsersOrFilterUpdate();
    }
  }

  applyUsersOrFilterUpdate() : void {
    const userFilterFieldProvider : MciUserFilterFieldProvider = new MciUserFilterFieldProvider(this.users);
    const configBuilder : QueryBuilderConfigProvider = new QueryBuilderConfigProvider(userFilterFieldProvider);
    this.queryConfig = configBuilder.getQueryBuilderConfig();
    const fs : StandardFilterStrategy<User> = new StandardFilterStrategy<User>(userFilterFieldProvider);
    this.filteredUsers = fs.applyFilter(this.users, this.currentFilter) ?? [];
    this.selectedUsers = [];
    this.updateSelectAllState();
  }

  loadUsers() {
    this.store.dispatch(new userActions.Load());
  }

  get mappedClientsForDropdown() {
    return this.clients.map(i => {
      return { Ref: i.Ref, Name: 'Ref: ' + i.Ref + ' - ' + i.Name };
    });
  }

  get mappedClientParentsForDropdown() {
    return this.clientParents.map(i => {
      return { Id: i.Id, Name: 'Ref: ' + i.Id + ' - ' + i.Name };
    });
  }


  showDialogToAdd() {
    this.userEditor.showDialog( new User(), true  );
  }

  isRowSelected(rowData: User) {

    if (this.selectedUsers.filter(x => {
        return x.id === rowData.id;
      }).length >
      0) {
      return true;
    }
    return false;
  }

  onRowSelect() {
    this.updateSelectAllState();
    this.onSelectionChanged.emit(this.selectedUsers);
  }

  onRowUnSelect() {
    this.updateSelectAllState();
    this.onSelectionChanged.emit(this.selectedUsers);
  }

  showDialogToEdit() {
    this.userEditor.showDialog(this.selectedUsers[0], false);
  }

  inviteUsers() {
    const inviteUserRequest = this.selectedUsers.map((user: User) => {
      return {
        Email: user.email,
        MojoPlatformUserId: user.mciUserId
      } as UserInviteRequest;
    });
    this.userService.inviteUsers(inviteUserRequest, this.brand).pipe(
      tap((result: UserInviteResult[]) => {

        const failed = result.filter(x => {
          return !x.success;
        });

        if (failed.length > 0) {
          const commaSeparatedEmails = failed.map((x) => {
            return x.emailAddress;
          }).join(',');
          this.messageService.error(`Invitations failed to send for the following users: ${commaSeparatedEmails}`, 'Unable to Invite users');
        } else {
          this.messageService.success("All invitations have been sent", 'User(s) Invited ');
        }
        this.loadUsers();
      }),
      catchError(() => {
        this.messageService.error(`Unable to invite users please try again.`, 'Unable to Invite users ');
        return of([]);
      }),
      takeWhile(() => this.isAlive)
    ).subscribe();
  }

  disableUsers() {
    const disableUserRequest = this.selectedUsers.map((user: User) => {
      return {
        Email: user.email,
        MojoPlatformUserId: user.mciUserId
      } as UserDisableRequest;
    });
    this.userService.disableUsers(disableUserRequest).pipe(
      tap((result: UserInviteResult[]) => {

        const failed = result.filter(x => {
          return !x.success;
        });

        if (failed.length > 0) {
          const commaSeparatedEmails = failed.map((x) => x.emailAddress).join(',');
          this.messageService.error(`Failed to deactivate the following users: ${commaSeparatedEmails}`, 'Unable to deactivate user(s)');
        } else {
          this.messageService.success("Selected User accounts have been deactivated", 'User(s) Deactivated');
        }
        this.selectedUsers = [];
        this.loadUsers();
      }),
      catchError(() => {
        this.messageService.error(`Unable to deactivate selected users please try again`, 'Unable to deactivate user(s) ');
        return of([]);
      }),
      takeWhile(() => this.isAlive)
    ).subscribe();
  }

  itemLevelColumns() {
      return this.getPrimeColumns().map(column => {return {Label: column.header, FieldName: column.field, Type: null}});
  }

  export = () : void =>
    this.exportCsvService.export(this.filteredUsers, this.itemLevelColumns());

  toggleRowSelection(rowData: User) {
    const rowSelected = this.isRowSelected(rowData);
    if (rowSelected) {
      this.selectedUsers = this.selectedUsers.filter(x => x.id !== rowData.id);
    } else {
      this.selectedUsers = [...this.selectedUsers, rowData];
    }
    this.updateSelectAllState();
    this.onSelectionChanged.emit(this.selectedUsers);
  }

  toggleSelectAll(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedUsers = [...this.filteredUsers];
    } else {
      this.selectedUsers = [];
    }
    this.onSelectionChanged.emit(this.selectedUsers);
    this.updateSelectAllState();
  }

  updateSelectAllState() {
    if (this.selectedUsers.length === this.filteredUsers.length && this.filteredUsers.length > 0) {
      this.selectAllState = 'all';
    } else if (this.selectedUsers.length > 0 && this.selectedUsers.length < this.filteredUsers.length) {
      this.selectAllState = 'indeterminate';
    } else {
      this.selectAllState = 'none';
    }
  }

  filterEdited($event: FilterEditorDialogResult) : void {
    this.store.dispatch(new filterActions.SaveListTypeFilter(new filterActions.ListTypeFilterActionPayload(this.listType, $event.filter)));
  }

  showFilterDialog(): void {
    this.filterDialog.showFilterDialog(this.currentFilter);
  }

  get hasCustomFilter(): boolean {
    return filterUtils.isNotDefaultFilter(this.currentFilter);
  }

}
